/**
 * @file    EnterpriseNavigationContext.tsx
 * @scope   ENTERPRISE
 * @summary Owns the Enterprise sub-view contexts (calculations / filters / partition filters).
 * @consumes common/types contexts
 * @produces EnterpriseNavigationProvider, useEnterpriseNavigation
 * @sideEffects None. Local state only.
 * @notes Moved out of `useDrawerNavigation` so the drawer stays unaware of Enterprise sub-views.
 */
import React, { createContext, useContext, useMemo, useState } from 'react';
import {
  AdvancedSettingsContext,
  FiltersSettingsContext,
  PartitionFiltersContext,
} from '../../../common/types';

export type EnterpriseNavigationValue = {
  advancedSettingsContext: AdvancedSettingsContext;
  setAdvancedSettingsContext: React.Dispatch<React.SetStateAction<AdvancedSettingsContext>>;
  filtersSettingsContext: FiltersSettingsContext;
  setFiltersSettingsContext: React.Dispatch<React.SetStateAction<FiltersSettingsContext>>;
  partitionFiltersContext: PartitionFiltersContext;
  setPartitionFiltersContext: React.Dispatch<React.SetStateAction<PartitionFiltersContext>>;
};

const EnterpriseNavigationContext = createContext<EnterpriseNavigationValue | null>(null);

export const EnterpriseNavigationProvider: React.FC<{ children: React.ReactNode }> = ({
  children,
}) => {
  const [advancedSettingsContext, setAdvancedSettingsContext] =
    useState<AdvancedSettingsContext>(null);
  const [filtersSettingsContext, setFiltersSettingsContext] =
    useState<FiltersSettingsContext>(null);
  const [partitionFiltersContext, setPartitionFiltersContext] =
    useState<PartitionFiltersContext>(null);

  const value = useMemo(
    () => ({
      advancedSettingsContext,
      setAdvancedSettingsContext,
      filtersSettingsContext,
      setFiltersSettingsContext,
      partitionFiltersContext,
      setPartitionFiltersContext,
    }),
    [advancedSettingsContext, filtersSettingsContext, partitionFiltersContext]
  );

  return (
    <EnterpriseNavigationContext.Provider value={value}>
      {children}
    </EnterpriseNavigationContext.Provider>
  );
};

export const useEnterpriseNavigation = (): EnterpriseNavigationValue => {
  const ctx = useContext(EnterpriseNavigationContext);
  if (!ctx) throw new Error('useEnterpriseNavigation must be used within its provider');
  return ctx;
};
